/**
 * Global concurrency gate for 3D neuroimage volume fetches (`get-mri-3d` and
 * `get-brain-3d`).
 *
 * Each volume costs hundreds of MB of server RAM to decode. The patient
 * workspace can request several neuroimages at once, and firing them
 * concurrently OOM-killed the web workers (#717). Serialising to one in-flight
 * request bounds the server's peak memory to a single volume at a time. This is
 * a belt-and-suspenders companion to the server-side native-dtype read fix:
 * even with per-request memory cut several-fold, N concurrent large volumes can
 * still overwhelm a memory-constrained host.
 */
const MAX_CONCURRENT_VOLUME_LOADS = 1;

let activeLoads = 0;
const waiting: Array<() => void> = [];

function pump(): void {
    while (activeLoads < MAX_CONCURRENT_VOLUME_LOADS && waiting.length > 0) {
        const start = waiting.shift()!;
        activeLoads += 1;
        start();
    }
}

/**
 * Run `task` under the shared volume-load concurrency limit. The returned
 * promise settles with the task's result (or rejection); the slot is always
 * released afterwards so a failed load never wedges the queue.
 *
 * @param task Thunk that starts the fetch and resolves with its payload.
 */
export function enqueueVolumeLoad<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        waiting.push(() => {
            task().then(resolve, reject).then(release, release);
        });
        pump();
    });
}

function release(): void {
    activeLoads -= 1;
    pump();
}
